import {Injectable} from '@angular/core';
import {UtilService} from '../util/util.service';
import {cloneDeep} from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  static MAX_32_INTEGER: number = 2147483647;
  static MAX_EMAIL_LENGTH: number = 100;

  static validateEmail(email: string): boolean {
    const re: RegExp = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    return !!email?.match(re) && (!email?.length || email?.length <= ValidationService.MAX_EMAIL_LENGTH);
  }

  static validatePhone(phone: string): boolean {
    return !!phone?.match('^[\+]?[0-9]+$');
  }

  static validateRegNumber(regNumber: string): boolean {
    return !!regNumber?.match('^[a-zA-Z0-9]+$');
  }

  static validatePassword(password: string): boolean {
    return !!password?.match('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
  }

  static validateString(value: string, minLength: number, maxLength?: number): boolean {
    const valueLength = value?.trim().length || 0;
    return (!minLength || valueLength >= minLength) && (!maxLength || valueLength <= maxLength);
  }

  static validateStringIsNotEmpty(value: string) {
    return value.trim().replace(' ', '') !== '';
  }

  static getStringModelAsText(model: string): string {
    const text = new DOMParser().parseFromString(model, 'text/html').firstChild?.textContent;
    return text != 'null' && text != 'undefined' ? text : '';
  }

  static validateUsername(username: string): boolean {
    return this.validateString(username, 3) && !!username.match('^[a-zA-Z0-9\-\_\.]+$');
  }

  static validateTime(hours: number, minutes: number): boolean {
    const timeString: string = hours.toString() + ':' + minutes.toString();
    return !!timeString?.match('^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$');
  }

  static validateUrl(url: string): boolean {
    return !!(!url || !!url?.match(
      '^(http[s]?:\\/\\/)?' + // scheme - http or https (optional)
      '(www(\\d?)\\.)?' + // www. or www1. (optional)
      '(((%[0-9A-Fa-f]{2}|[-A-Za-z0-9]){1,63})\\.){1,500}[A-Za-z]{2,6}' + // domain (at least 1, max 500) + tld (mandatory),
      '(:\\d{1,5})?' + // port (optional)
      '(\\/(#|%[0-9A-Fa-f]{2}|[-A-Za-z0-9])+)*' + // directories & paths (optional)
      '(\\/)?' + // path slash at the end (optional)
      '((\\?[^=&]+=[^=&]*)+(\\&[^=&]+=[^=&]*)*)?' + // query & parameters (optional)
      '(#[^=&]+)*$' // fragments (optional)
    ));
  }

  static formatCommasFromString(price: string | number): string {
    if (typeof price === 'number') {
      price = price.toString();
    }
    price = price.replace(',', '.');
    if (price.startsWith('.')) {
      price = '0' + price;
    }
    return price.replace('/^.+/', '').replace('/.+$/', '');
  }
  static validatePrice(price: string | number): boolean {
    const validationValue: string = (price != null) ? price.toString() : '';
    return !!this.formatCommasFromString(validationValue)?.match('^\\d+(\\.\\d{1,2})?$');
  }

  static isArrowKeysPressed(keyboardEvent: KeyboardEvent): boolean {
    return keyboardEvent && (
      keyboardEvent.key === UtilService.KEY_LEFT_ARROW ||
      keyboardEvent.key === UtilService.KEY_UP_ARROW ||
      keyboardEvent.key === UtilService.KEY_RIGHT_ARROW ||
      keyboardEvent.key === UtilService.KEY_DOWN_ARROW
    );
  }

  static isEnterOrArrowKeysPressed(keyboardEvent: KeyboardEvent): boolean {
    return keyboardEvent && (this.isArrowKeysPressed(keyboardEvent) || keyboardEvent.key === UtilService.KEY_ENTER);
  }

  static validatePercentage(value: string | number, allowDecimalPlaces?: boolean): boolean {
    const regex: RegExp = /^\d+$/;
    const decimalRegex: RegExp = /^\d+(\.\d{1,2})?$/;
    const validationValue: string = value ? cloneDeep(value.toString()) : '';
    return !validationValue || (!!validationValue.match(allowDecimalPlaces ? decimalRegex : regex) && Number(value) <= 100 && Number(value) >= 0);
  }

  public emptyOrNumeric(value: number | string, ignoreMax32?: boolean): boolean {
    const regex: RegExp = /^\d+$/;
    const validationValue: string = value ? value.toString() : '';
    return !validationValue || (!!validationValue.match(regex) && (ignoreMax32 || Number(value) <= ValidationService.MAX_32_INTEGER) && Number(value) >= 0);
  }
  static validateNumberFromString(value: string, emptyAllowed: boolean = true): boolean {
    const regex: RegExp = /^\d+$/;
    return (emptyAllowed && !value) || !!value.match(regex);
  }

  public emptyOrAlphaNumeric(value: number | string): boolean {
    const regex: RegExp = /^\w*$/;
    const validationValue: string = value ? value.toString() : '';
    return !validationValue || !!validationValue.match(regex);
  }

  public anyOfNotValid(values: any[], method: (value: any, length?: number) => boolean, length?: number): boolean {
    if (length) {
      return values && !!values.filter(v => !method(v, length)).length;
    }
    return values && !!values.filter(v => !method(v)).length;
  }
}
